import { Flame, Heart } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';
import { CourseSwitcherDropdown } from './CourseSwitcherDropdown';

interface TopBarProps {
  courseTitle: string;
  xp: number;
  xpToNextLevel: number;
  progressToNextLevel: number;
  level: number;
  streak?: number;
  completedToday?: boolean;
  hearts?: number;
  totalPages?: number;
  currentPage?: number;
}

export function TopBar({
  courseTitle,
  xp,
  xpToNextLevel,
  progressToNextLevel,
  level,
  streak = 0,
  completedToday = false,
  hearts = 5,
  totalPages,
  currentPage
}: TopBarProps) {
  const showLessonProgress = typeof totalPages === 'number' && totalPages > 0 && typeof currentPage === 'number';
  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Course Switcher */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="min-w-0 flex-1">
              <CourseSwitcherDropdown currentCourseTitle={courseTitle} />
              {showLessonProgress && (
                <div className="mt-2 flex items-center gap-1.5">
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const active = index <= currentPage;
                    return (
                      <span
                        key={index}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${active ? 'bg-primary' : 'bg-border/60'}`}
                        aria-hidden="true"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: Stats with even gap spacing */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Level & XP */}
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-1 md:gap-2 text-[10px] sm:text-xs md:text-sm font-semibold">
                <span className="text-primary">Level {level}</span>
                <span className="text-muted-foreground opacity-50">|</span>
                <span className="text-primary">{xp} XP</span>
              </div>
              <div className="mt-0.5 w-20 sm:w-24 md:mt-1 md:w-32">
                <Progress
                  value={progressToNextLevel}
                  className="h-1 md:h-1.5"
                  aria-label={`${xpToNextLevel} XP remaining to reach level ${level + 1}`}
                />
                <p className="mt-0.5 whitespace-nowrap text-right text-[9px] text-muted-foreground sm:text-[10px]">
                  {xpToNextLevel} XP to Level {level + 1}
                </p>
              </div>
            </div>

            {/* Streak */}
            {streak >= 0 && (
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border transition-all duration-300"
                style={{
                  background: completedToday ? 'rgba(249, 115, 22, 0.1)' : 'rgba(71, 85, 105, 0.1)',
                  borderColor: completedToday ? 'rgba(249, 115, 22, 0.2)' : 'rgba(71, 85, 105, 0.2)',
                  color: completedToday ? '#FB923C' : '#94A3B8',
                }}
              >
                <Flame
                  className="w-4 h-4 transition-all duration-300"
                  style={{
                    color: completedToday ? '#FB923C' : '#475569',
                    fill: completedToday ? '#FB923C' : '#475569',
                  }}
                />
                <span>{streak}</span>
              </div>
            )}

            {/* Hearts */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-bold border border-red-500/20">
              <Heart className="w-4 h-4 fill-current" />
              <span>{hearts}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
